import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { RmTemplateRatingTypeSchema } from '@brightlocal/shared';

import { TEMPLATE_CONTENT_LENGTH, TEMPLATE_FORM_FIELDS, TEMPLATE_NAME_LENGTH } from '../consts';
import { findUnknownPlaceholder } from '../utils/findUnknownPlaceholder';
import { isTemplateTextLength } from '../utils/isTemplateTextLength';

/** Rules of the template form, as the API applies them. */
export const useTemplateFormSchema = () => {
  const { t } = useTranslation();
  const nameLengthError = t('reports.replyTemplates.sheet.errors.nameLength', {
    min: TEMPLATE_NAME_LENGTH.MIN,
    max: TEMPLATE_NAME_LENGTH.MAX
  });
  const contentLengthError = t('reports.replyTemplates.sheet.errors.contentLength', {
    min: TEMPLATE_CONTENT_LENGTH.MIN,
    max: TEMPLATE_CONTENT_LENGTH.MAX
  });

  return z.object({
    [TEMPLATE_FORM_FIELDS.NAME]: z
      .string()
      .refine(
        (name) => isTemplateTextLength({ value: name, min: TEMPLATE_NAME_LENGTH.MIN, max: TEMPLATE_NAME_LENGTH.MAX }),
        nameLengthError
      ),
    [TEMPLATE_FORM_FIELDS.CONTENT]: z
      .string()
      .refine(
        (content) =>
          isTemplateTextLength({ value: content, min: TEMPLATE_CONTENT_LENGTH.MIN, max: TEMPLATE_CONTENT_LENGTH.MAX }),
        contentLengthError
      )
      .superRefine((content, context) => {
        const placeholder = findUnknownPlaceholder(content);

        if (placeholder !== undefined) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('reports.replyTemplates.sheet.errors.unknownPlaceholder', { placeholder })
          });
        }
      }),
    [TEMPLATE_FORM_FIELDS.RATING]: RmTemplateRatingTypeSchema
  });
};

/** Values of the template form. */
export type TemplateFormValues = z.infer<ReturnType<typeof useTemplateFormSchema>>;
